import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks, parseISO, differenceInCalendarDays } from "date-fns";
import { fr } from "date-fns/locale";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PlanningDialog } from "@/components/PlanningDialog";
import { CAT_META, mqToday, fmtHour, fmtShortDate } from "@/lib/planning";
import * as Icons from "lucide-react";

const WEEKDAYS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];
const isScheduled = (w) => w.schedule_confirmed && w.planned_start && w.category !== "a_planifier";

function buildDays(mode, cursor) {
  const days = [];
  const [s, e] = mode === "month"
    ? [startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })]
    : [startOfWeek(cursor, { weekStartsOn: 1 }), endOfWeek(cursor, { weekStartsOn: 1 })];
  for (let d = s; d <= e; d = addDays(d, 1)) days.push(d);
  return days;
}

function WsChip({ w, day, onOpen, wrap, draggable }) {
  const m = CAT_META[w.category] || CAT_META.termine;
  const first = day === w.planned_start;
  const multi = (w.planned_end || w.planned_start) !== w.planned_start;
  return (
    <button onClick={() => onOpen(`/track/${w.id}`)} data-testid={`planning-ws-${w.reference}-${day}`}
      draggable={draggable} onDragStart={draggable ? (e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", JSON.stringify({ id: w.id, from: day })); } : undefined}
      title={draggable ? "Glissez vers un autre jour pour replanifier" : undefined}
      className={`flex w-full items-center gap-1 rounded border-l-[3px] px-1.5 py-1 text-left text-[11px] text-slate-700 hover:brightness-95 ${wrap ? "" : "truncate"} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${m.chip}`}>
      <Icons.HardHat className="h-3 w-3 shrink-0 text-slate-500" />
      {first && !w.all_day && w.start_time && <span className="shrink-0 whitespace-nowrap font-mono-data text-[10px] text-slate-500">{fmtHour(w.start_time)}</span>}
      <span className={wrap ? "line-clamp-2 break-words" : "truncate"}>{!first && multi ? "↳ " : ""}{w.title}</span>
    </button>
  );
}

function VisitChip({ v, onOpen, wrap }) {
  return (
    <button onClick={() => onOpen(`/check/${v.id}`)} data-testid={`planning-visit-${v.reference}`}
      className={`flex w-full items-center gap-1 rounded border border-dashed border-violet-300 bg-white px-1.5 py-1 text-left text-[11px] text-violet-800 hover:bg-violet-50 ${wrap ? "" : "truncate"}`}>
      <Icons.ClipboardCheck className="h-3 w-3 shrink-0" />
      {v.scheduled_time && <span className="font-mono-data text-[10px]">{v.scheduled_time}</span>}
      <span className={wrap ? "line-clamp-2 break-words" : "truncate"}>Visite · {v.client_name || v.reference}</span>
    </button>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500" data-testid="planning-legend">
      {["planifie", "en_cours", "en_pause", "termine"].map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${CAT_META[k].dot}`} />Chantier {CAT_META[k].short.toLowerCase()}</span>)}
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm border border-dashed border-violet-400" />Visite</span>
    </div>
  );
}

export default function Planning() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("track.manage");
  const [ws, setWs] = useState(null);
  const [visits, setVisits] = useState([]);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [planning, setPlanning] = useState(null);
  const [dropKey, setDropKey] = useState(null);
  const [moving, setMoving] = useState(false);
  const today = mqToday();

  const load = useCallback(() => {
    setError(false); setWs(null);
    api.get("/track/planning").then((r) => setWs(r.data.items || [])).catch(() => setError(true));
    api.get("/visits").then((r) => setVisits(r.data || [])).catch(() => setVisits([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const days = buildDays(mode, cursor);
  const keys = days.map((d) => format(d, "yyyy-MM-dd"));
  const scheduled = (ws || []).filter(isScheduled);
  const unscheduled = (ws || []).filter((w) => !isScheduled(w) && w.category !== "termine");
  const wsOn = (k) => scheduled.filter((w) => w.planned_start <= k && k <= (w.planned_end && w.planned_end >= w.planned_start ? w.planned_end : w.planned_start))
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const visitsOn = (k) => visits.filter((v) => v.date === k).sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  const title = mode === "month" ? format(cursor, "MMMM yyyy", { locale: fr })
    : `${format(days[0], "d MMM", { locale: fr })} – ${format(days[6], "d MMM yyyy", { locale: fr })}`;
  const shift = (dir) => setCursor((c) => (mode === "month" ? addMonths(c, dir) : addWeeks(c, dir)));
  const max = mode === "week" ? 20 : 3;
  const canDrag = (w) => canManage && !moving && w.category === "planifie";

  const moveTo = async (e, target) => {
    e.preventDefault(); setDropKey(null);
    let p; try { p = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    const w = (ws || []).find((x) => x.id === p?.id);
    if (!w || !canDrag(w)) return;
    const delta = differenceInCalendarDays(parseISO(target), parseISO(p.from));
    if (!delta) return;
    const shiftD = (d) => (d ? format(addDays(parseISO(d), delta), "yyyy-MM-dd") : null);
    const start = shiftD(w.planned_start);
    setMoving(true);
    try {
      await api.post(`/track/worksites/${w.id}/schedule`, {
        planned_start: start, all_day: w.all_day, start_time: w.all_day ? null : w.start_time || "08:00",
        planned_end: shiftD(w.planned_end), end_time: w.end_time || null,
        responsible_id: w.responsible_id || null, planning_note: w.planning_note || "",
      });
      toast.success(`${w.title} replanifié au ${fmtShortDate(start)}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setMoving(false); }
  };

  return (
    <div className="space-y-5" data-testid="planning-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={() => navigate("/")} className="mb-1 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="planning-back"><Icons.ChevronLeft className="h-4 w-4" />Tableau de bord</button>
          <h1 className="font-display text-2xl font-bold text-[#0D2E24] lg:text-3xl">Planning</h1>
          <p className="text-sm text-slate-500">Chantiers SIGNA TRACK et visites SIGNA CHECK, aux dates prévues.</p>
        </div>
      </div>

      <div className="grid gap-5">
        <Card className="p-3 lg:p-4" data-testid="planning-calendar">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="planning-prev"><Icons.ChevronLeft className="h-4 w-4" /></Button>
              <span className="min-w-[150px] text-center font-display text-base font-semibold text-slate-800" data-testid="planning-title">{title}</span>
              <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="planning-next"><Icons.ChevronRight className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} className="ml-1" data-testid="planning-today">Aujourd'hui</Button>
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              {[["week", "Semaine"], ["month", "Mois"]].map(([k, l]) => (
                <button key={k} onClick={() => setMode(k)} data-testid={`planning-mode-${k}`}
                  className={`rounded-md px-3 py-1 text-sm font-medium ${mode === k ? "bg-emerald-600 text-white" : "text-slate-600"}`}>{l}</button>))}
            </div>
          </div>
          {error ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center" data-testid="planning-error">
              <p className="text-sm text-rose-600">Impossible de charger le planning.</p>
              <Button variant="outline" size="sm" onClick={load} data-testid="planning-retry"><Icons.RotateCw className="mr-1.5 h-4 w-4" />Réessayer</Button>
            </div>
          ) : !ws ? (
            <div className="h-64 animate-pulse rounded-lg bg-slate-100" data-testid="planning-loading" />
          ) : (
            <>
              <div className="hidden grid-cols-7 gap-1 md:grid">
                {WEEKDAYS.map((w) => <div key={w} className="pb-1 text-center text-[11px] font-semibold uppercase text-slate-400">{w}</div>)}
                {days.map((day, i) => {
                  const k = keys[i];
                  const list = [...wsOn(k).map((w) => ({ t: "w", w })), ...visitsOn(k).map((v) => ({ t: "v", v }))];
                  const outside = mode === "month" && day.getMonth() !== cursor.getMonth();
                  return (
                    <div key={k} data-testid={`planning-day-${k}`}
                      onDragOver={canManage ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropKey !== k) setDropKey(k); } : undefined}
                      onDragLeave={canManage ? () => setDropKey((d) => (d === k ? null : d)) : undefined}
                      onDrop={canManage ? (e) => moveTo(e, k) : undefined}
                      className={`rounded-lg border p-1 transition-colors ${dropKey === k ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200" : outside ? "border-slate-100 bg-slate-50/40" : "border-slate-200 bg-white"} ${mode === "week" ? "min-h-[260px]" : "min-h-[96px]"}`}>
                      <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${k === today ? "bg-emerald-600 text-white" : outside ? "text-slate-300" : "text-slate-600"}`}>{format(day, "d")}</div>
                      <div className="space-y-1">
                        {list.slice(0, max).map((x) => x.t === "w" ? <WsChip key={`w${x.w.id}`} w={x.w} day={k} onOpen={navigate} wrap={mode === "week"} draggable={canDrag(x.w)} /> : <VisitChip key={`v${x.v.id}`} v={x.v} onOpen={navigate} wrap={mode === "week"} />)}
                        {list.length > max && <span className="block px-1 text-[10px] text-slate-400">+{list.length - max} autre(s)</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-2 md:hidden" data-testid="planning-agenda">
                {keys.filter((k) => (mode === "week" || k.slice(0, 7) === format(cursor, "yyyy-MM")) && (wsOn(k).length || visitsOn(k).length)).map((k) => (
                  <div key={k} className="rounded-lg border border-slate-200 p-2">
                    <p className={`mb-1.5 text-xs font-semibold ${k === today ? "text-emerald-700" : "text-slate-600"}`}>{format(new Date(`${k}T12:00:00`), "EEEE d MMMM", { locale: fr })}</p>
                    <div className="space-y-1">{wsOn(k).map((w) => <WsChip key={w.id} w={w} day={k} onOpen={navigate} />)}{visitsOn(k).map((v) => <VisitChip key={v.id} v={v} onOpen={navigate} />)}</div>
                  </div>))}
                {!keys.some((k) => wsOn(k).length || visitsOn(k).length) && <p className="py-6 text-center text-sm text-slate-400" data-testid="planning-agenda-empty">Rien de prévu sur cette période.</p>}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2"><Legend />
                {canManage && <p className="hidden text-xs text-slate-400 md:block" data-testid="planning-drag-hint"><Icons.Move className="mr-1 inline h-3.5 w-3.5" />Glissez un chantier planifié vers un autre jour pour le replanifier (durée et heures conservées).</p>}
              </div>
            </>
          )}
        </Card>

        <Card className="h-fit overflow-hidden" data-testid="planning-unscheduled">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#0D2E24]"><Icons.CalendarPlus className="h-4 w-4 text-orange-500" />À planifier {ws ? `(${unscheduled.length})` : ""}</h2>
            <p className="text-xs text-slate-400">Chantiers sans créneau confirmé — aucune date n'est attribuée.</p>
          </div>
          {!ws ? <div className="p-4 text-sm text-slate-400">{error ? "—" : "Chargement…"}</div>
            : unscheduled.length === 0 ? <p className="p-4 text-sm text-slate-400" data-testid="planning-unscheduled-empty">Aucun chantier à planifier.</p>
            : <ul className="divide-y divide-slate-100">{unscheduled.map((w) => (
              <li key={w.id} className="flex items-center gap-2 px-4 py-2.5" data-testid={`planning-unscheduled-${w.reference}`}>
                <button onClick={() => navigate(`/track/${w.id}`)} className="min-w-0 flex-1 text-left">
                  <p className="truncate text-sm font-medium text-slate-800">{w.title}</p>
                  <p className="truncate text-xs text-slate-500">{w.client_name} · {w.site_name}{w.planned_start && !w.schedule_confirmed ? ` · souhaité le ${fmtShortDate(w.planned_start)}` : ""}</p>
                </button>
                {canManage && <Button size="sm" variant="outline" onClick={() => setPlanning(w)} className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`planning-plan-${w.reference}`}>Planifier</Button>}
              </li>))}</ul>}
        </Card>
      </div>
      <PlanningDialog open={!!planning} onOpenChange={(o) => !o && setPlanning(null)} ws={planning} onSaved={load} />
    </div>
  );
}
